/**
 * Echo Hunt -- the lore generator prompt (P10.5, §8.5 + §8.7).
 *
 * VISIBILITY-MATRIX ROW (§16.0): "Lore generator". This prompt is OUTSIDE the game pipeline: it drafts a
 * world or a character at the user's request from the Worlds/Characters tabs, and is never called while
 * a round is being built. It therefore cannot leak anything into the hunt.
 *
 * Its one hard rule is negative: it is told NOTHING about the corpus, the journal, the user's dreams or
 * the bullets. A world or character written to fit the user's journal would be the invariant #4
 * asymmetry in a different costume -- the weaver adapts the real bullets to it, so it should have been
 * written without them.
 *
 * ## Why one prompt serves four request shapes
 *
 * The editor can draft the WHOLE entry, or one field at a time (name, disposition, notes). Rather than
 * four prompts that can drift apart, there is one prompt with a `Drafting only` section naming the
 * fields to change, and the reply ALWAYS carries the full object. `Lore.ts` then copies only the
 * requested fields onto the caller's draft, so a model that helpfully rewrites the disposition while
 * filling in a name cannot silently change a value the user is happy with.
 *
 * ## Register examples
 *
 * The rng pools in `Worlds.ts` (names, trades/habits, the disposition-band notes) are passed in as
 * EXAMPLES, not as content to copy. §8.2 wants a character with one trade and one clearly-stated
 * disposition, written as habits rather than as trait adjectives -- and those pools are exactly the
 * register that shipped in P8. Without them, "generate a personality note" trends toward generic
 * character-sheet prose ("loyal, quick-witted, haunted by her past"), which the weaver then has to obey
 * literally.
 *
 * See `Docs/Plans/Others/2026-10-19_EchoHunt.md` §8.2, §8.5, §8.7, §16.0.
 */
import type {JSONSchemaLike} from "../LLM.js";
import {CHARACTER_DISPOSITION_NOTES, CHARACTER_NAMES, CHARACTER_ROLES} from "../Worlds.js";
import {DescribeCautionVsOpportunity, DescribeCrazinessTolerance} from "./Weave.js";

/** Which part of a character an autogen request should replace. `all` drafts a whole entry, which is
 * what an empty editor asks for. */
export const LoreCharacterField_values = ["all", "name", "tagline", "disposition", "notes"] as const;
export type LoreCharacterField = typeof LoreCharacterField_values[number];

/** Which part of a world an autogen request should replace. */
export const LoreWorldField_values = ["all", "name", "bible", "tolerance"] as const;
export type LoreWorldField = typeof LoreWorldField_values[number];

// shared
// ==========

/** Rules that apply to BOTH kinds of lore. Stated as prohibitions of the recognizable generated-lore
 * register, for the same reason `FAKE_CLICHE_RULES` is (§5.5): the register, not any single word, is
 * what makes machine-written material obvious. */
export const LORE_RULES = [
	"No glowing, humming, shimmering or ancient anything; no prophecy, no destiny, no chosen one.",
	"No lore-speak: no \"it is said that\", no \"long ago\", no \"the old ones\", no faux-archaic phrasing.",
	"No tidy symbolism, no moral, no grand significance attached to any object.",
	"Write plainly and concretely. Name specific trades, objects, foods, rooms, rules and inconveniences.",
	"Specific and a little dull is good. This is a place and a person to be inhabited, not a pitch.",
	"Never mention dreams, sleep, waking, lucidity or the fact that any of this is a game.",
].map(a=>`- ${a}`).join("\n");

/** Never stated to the model; kept here so the negative rule above has a written counterpart. */
export const LORE_VISIBILITY_NOTE = "The lore prompt receives no corpus, no journal text and no bullets -- authoring material must be independent of what the user dreams.";

function CharacterRegisterExamples(): string {
	const roles = CHARACTER_ROLES.slice(0, 6).map(a=>`- ${a.role}: ${a.habit}`).join("\n");
	const names = CHARACTER_NAMES.slice(0, 6).join(", ");
	const notes = CHARACTER_DISPOSITION_NOTES.map(band=>{
		const sample = band.notes[0];
		return `- disposition <= ${band.max}: "${sample}"`;
	}).join("\n");
	return [
		"## The register we write in",
		"These are examples of the SHAPE and PLAINNESS wanted. They are NOT a menu: do not reuse any of",
		"them. A repeated name is the single most obvious sign that a character was machine-drafted, so",
		"invent a different ordinary name every time and never repeat one from this list.",
		"",
		"Names (ordinary, varied, not exotic; do NOT reuse these):",
		names,
		"",
		"Trades and habits -- a character has ONE ordinary competence, phrased as a habit. Match the SHAPE;",
		"do not copy a line:",
		roles,
		"",
		"A personality note follows FROM the disposition, as a habit rather than an adjective:",
		notes,
	].join("\n");
}

function WorldRegisterExamples(): string {
	return [
		"## The register we write in",
		"A world bible is a set of CONSTRAINTS the weaver can obey: what kinds of place exist, who lives",
		"there, what is ordinary, what is impossible, and what must not be contradicted. Never a plot",
		"summary -- a plot invites scenes to be written from the summary instead of from the events.",
		"",
		"Bibles run 100-200 words. They are matter-of-fact, not atmospheric, and they say plainly that",
		"nobody in the world finds its impossibilities remarkable.",
	].join("\n");
}

// characters
// ==========

export function LoreCharacter_SystemPrompt(): string {
	return [
		"You draft a supporting character for a story: a name, a one-line tagline, a disposition and a few",
		"plain personality notes.",
		"The character is an ordinary person with a trade, not a hero and not a mystery.",
		"Reply with JSON only. No prose, no code fences, no explanation.",
	].join("\n");
}

/** The keys a character request may write. Used instead of the `LoreCharacterField` request verb so the
 * prompt can name EXACTLY the fields being drafted -- the difference between "fill what is empty" and
 * "redraft everything" has to be visible to the model, or it echoes the draft back. */
export type CharacterDraftKey = "name"|"tagline"|"values_cautionVsOpportunity"|"personalityNotes";

const CHARACTER_KEY_LABELS: Record<CharacterDraftKey, string> = {
	name: "name",
	tagline: "tagline",
	values_cautionVsOpportunity: "disposition",
	personalityNotes: "personality notes",
};

export type LoreCharacter_UserPromptOptions = {
	/** the field(s) the user asked for; every other field in the reply is ignored by `Lore.ts` */
	field: LoreCharacterField;
	/** the exact keys that must be written fresh. `Lore.ts` narrows `all` to the EMPTY fields unless the
	 * user ticked Override. Defaults to `field`'s own set, so a direct caller still gets a sane prompt. */
	drafting?: CharacterDraftKey[]|n;
	/** true = replace the current values; false/absent = keep them and fill only what is missing */
	override?: boolean;
	/** the current draft, so a per-field request can stay consistent with what is already there */
	draft?: {name?: string|n, tagline?: string|n, values_cautionVsOpportunity?: number|n, personalityNotes?: string|n}|n;
	/** the user's free-text steer, e.g. "retired lighthouse keeper, quietly stubborn" */
	hint?: string|n;
	/** names already in the pool (lowercased). Listed as forbidden, because an example-name list is the
	 * thing a model drifts back to when it has nothing else to anchor on. */
	avoidNames?: string[]|n;
	/** on a retry: the problems found in the previous reply */
	fixNotes?: string|n;
};

export function LoreCharacter_UserPrompt(opts: LoreCharacter_UserPromptOptions): string {
	const {field, draft, hint, fixNotes} = opts;
	const drafting = opts.drafting ?? (field == "all"
		? (["name", "tagline", "values_cautionVsOpportunity", "personalityNotes"] as CharacterDraftKey[])
		: [field as CharacterDraftKey]);

	const draftingLabels = drafting.map(key=>CHARACTER_KEY_LABELS[key]);
	// "write something DIFFERENT" only applies when an EXISTING value is being replaced. A per-field
	// request on an empty field, or a completion run that narrowed `drafting` to the blanks, gets the
	// "only empty fields" wording -- telling a model not to reuse text it was never shown is nonsense.
	const redrawing = drafting.some(key=>{
		switch (key) {
			case "name": return (draft?.name ?? "").trim().length > 0;
			case "tagline": return (draft?.tagline ?? "").trim().length > 0;
			case "personalityNotes": return (draft?.personalityNotes ?? "").trim().length > 0;
			// any number is a value, including 0 -- the axis is redrawn whenever it is asked for
			case "values_cautionVsOpportunity": return draft?.values_cautionVsOpportunity != null;
		}
	});

	const parts = [
		"## Task",
		drafting.length == 4
			? "Draft a character: a name, a one-line tagline, a disposition and personality notes."
			: `Write ${draftingLabels.join(", ")} for this character. Leave the other fields exactly as they are.`,
		"",
		"## The disposition axis",
		"-1 is very cautious (avoids risk, checks things, would rather leave than push).",
		"+1 is very opportunistic (acts first, uses whatever is offered, rarely asks whether it should).",
		"Draw widely across the range. A pool that clusters near 0 makes every character behave the same way.",
		"",
		"## What a personality note is",
		"A concrete HABIT or preference that shows up in what the character does -- not an adjective and not a",
		"backstory. Give them one ordinary competence from a trade, plus one or two notes that follow from",
		"their disposition, so the notes cannot contradict the number.",
		"",
		CharacterRegisterExamples(),
	];

	if (hint != null && hint.trim().length > 0) {
		parts.push(
			"",
			"## The user's steer",
			"Follow this where it does not conflict with the rules above. If it names a trade or a temperament,",
			"that trade or temperament is REQUIRED.",
			hint.trim(),
		);
	}

	if ((opts.avoidNames ?? []).length > 0) {
		parts.push(
			"",
			"## Names that are already taken",
			"These characters already exist. Do NOT use any of these names, and do not use a variant of one:",
			...(opts.avoidNames ?? []).map(name=>`- ${name}`),
		);
	}

	const hasDraft = draft != null && [
		draft.name, draft.tagline, draft.personalityNotes,
		draft.values_cautionVsOpportunity != null ? `${draft.values_cautionVsOpportunity}` : null,
	].some(a=>(a ?? "").trim().length > 0);
	if (hasDraft) {
		parts.push(
			"",
			"## The current draft",
			draft?.name != null && draft.name.trim().length > 0 ? `Name: ${draft.name.trim()}` : "Name: (empty)",
			draft?.tagline != null && draft.tagline.trim().length > 0 ? `Tagline: ${draft.tagline.trim()}` : "Tagline: (empty)",
			draft?.values_cautionVsOpportunity != null
				? `Disposition: ${draft.values_cautionVsOpportunity} (${DescribeCautionVsOpportunity(draft.values_cautionVsOpportunity)})`
				: "Disposition: (unset)",
			draft?.personalityNotes != null && draft.personalityNotes.trim().length > 0
				? `Notes: ${draft.personalityNotes.trim()}`
				: "Notes: (empty)",
		);

		if (redrawing) {
			// without this, a model handed its own previous output simply reproduces it, and pressing Draft a
			// second time appears to do nothing at all
			parts.push(
				"",
				`Write a DIFFERENT value for: ${draftingLabels.join(", ")}.`,
				"Do not reuse, copy or lightly paraphrase the current value of a field you are drafting -- the user",
				"pressed the button precisely because they want another option. The current draft is context for",
				"staying consistent, not text to reproduce.",
				"Only the fields you are drafting may change; the others must be returned exactly as they read here.",
			);
		} else {
			parts.push("", "Keep everything as it reads here -- only empty fields need filling in.");
		}
	}

	parts.push(
		"",
		"## Do not do any of this",
		LORE_RULES,
		"",
		"## Output",
		"Reply with ONE JSON object containing ALL FOUR fields, even when only one was requested:",
		`{"name": "...", "tagline": "...", "values_cautionVsOpportunity": -1.0..1.0, "personalityNotes": "..."}`,
		"`tagline` is one short sentence (max ~15 words) that says who they are and what they are like.",
		"`personalityNotes` is 1-3 sentences.",
	);

	if (fixNotes != null && fixNotes.trim().length > 0) {
		parts.push("", "## Fix this problem from your previous attempt", fixNotes.trim());
	}

	return parts.join("\n");
}

export function LoreCharacter_JSONSchema(): JSONSchemaLike {
	return {
		type: "object",
		required: ["name", "tagline", "values_cautionVsOpportunity", "personalityNotes"],
		properties: {
			name: {type: "string", minLength: 1},
			tagline: {type: "string", minLength: 1},
			// deliberately unbounded here: `Lore.ts` clamps into [-1, 1] rather than rejecting a reply
			// that is simply out of range, which is the same posture the rest of the pipeline takes
			values_cautionVsOpportunity: {type: "number"},
			personalityNotes: {type: "string", minLength: 1},
		},
	};
}

// worlds
// ==========

export function LoreWorld_SystemPrompt(): string {
	return [
		"You draft a setting for a story: a short, plain bible of the place's own rules.",
		"The place may be impossible in its own consistent ways. Nobody who lives there finds that remarkable.",
		"Reply with JSON only. No prose, no code fences, no explanation.",
	].join("\n");
}

/** The keys a world request may write (see `CharacterDraftKey`). */
export type WorldDraftKey = "name"|"bible"|"crazinessTolerance";

const WORLD_KEY_LABELS: Record<WorldDraftKey, string> = {
	name: "name",
	bible: "bible",
	crazinessTolerance: "craziness tolerance",
};

export type LoreWorld_UserPromptOptions = {
	field: LoreWorldField;
	/** the exact keys that must be written fresh; `Lore.ts` narrows `all` to the EMPTY fields unless the
	 * user ticked Override. Defaults to `field`'s own set. */
	drafting?: WorldDraftKey[]|n;
	/** true = replace the current values; false/absent = keep them and fill only what is missing */
	override?: boolean;
	draft?: {name?: string|n, bible?: string|n, crazinessTolerance?: number|n}|n;
	hint?: string|n;
	fixNotes?: string|n;
};

export function LoreWorld_UserPrompt(opts: LoreWorld_UserPromptOptions): string {
	const {field, draft, hint, fixNotes} = opts;
	const drafting = opts.drafting ?? (field == "all"
		? (["name", "bible", "crazinessTolerance"] as WorldDraftKey[])
		: [field as WorldDraftKey]);

	const draftingLabels = drafting.map(key=>WORLD_KEY_LABELS[key]);
	const redrawing = drafting.some(key=>{
		switch (key) {
			case "name": return (draft?.name ?? "").trim().length > 0;
			case "bible": return (draft?.bible ?? "").trim().length > 0;
			case "crazinessTolerance": return draft?.crazinessTolerance != null;
		}
	});

	const parts = [
		"## Task",
		drafting.length == 3
			? "Draft a setting: a name, a bible and a craziness tolerance."
			: `Write ${draftingLabels.join(", ")} for this setting. Leave the other fields exactly as they are.`,
		"",
		"## The tolerance axis",
		"0 = nearly an ordinary world, so impossible events stand out in it.",
		"1 = anything can happen here and nobody in the story finds it remarkable.",
		"Prefer high tolerance: a world that cannot absorb a wild event forces the story to smooth it into",
		"something ordinary, which is exactly how a strange event gets eroded.",
		"",
		"## Two things to avoid",
		"- No setting that is ABOUT dreams or dreaming. It blurs what the story is testing.",
		"- No setting the reader has to already know. Write it so the bible alone is enough.",
		"",
		WorldRegisterExamples(),
	];

	if (hint != null && hint.trim().length > 0) {
		parts.push(
			"",
			"## The user's steer",
			"Follow this where it does not conflict with the rules above.",
			hint.trim(),
		);
	}

	const hasDraft = draft != null && [draft.name, draft.bible].some(a=>(a ?? "").trim().length > 0);
	if (hasDraft) {
		parts.push(
			"",
			"## The current draft",
			draft?.name != null && draft.name.trim().length > 0 ? `Name: ${draft.name.trim()}` : "Name: (empty)",
			draft?.crazinessTolerance != null
				? `Tolerance: ${draft.crazinessTolerance} (${DescribeCrazinessTolerance(draft.crazinessTolerance)})`
				: "Tolerance: (unset)",
			draft?.bible != null && draft.bible.trim().length > 0 ? ["Bible:", draft.bible.trim()].join("\n") : "Bible: (empty)",
		);

		if (redrawing) {
			parts.push(
				"",
				`Write a DIFFERENT value for: ${draftingLabels.join(", ")}.`,
				"Do not reuse, copy or lightly paraphrase the current value of a field you are drafting -- the user",
				"pressed the button precisely because they want another option. The current draft is context for",
				"staying consistent, not text to reproduce.",
				"Only the fields you are drafting may change; the others must be returned exactly as they read here.",
			);
		} else {
			parts.push("", "Keep everything as it reads here -- only empty fields need filling in.");
		}
	}

	parts.push(
		"",
		"## Do not do any of this",
		LORE_RULES,
		"",
		"## Output",
		"Reply with ONE JSON object containing ALL THREE fields, even when only one was requested:",
		`{"name": "...", "bible": "...", "crazinessTolerance": 0.0..1.0}`,
		"The bible must state: what the place is, what is ordinary there, what is impossible there, and the",
		"one or two facts that must not be contradicted. 100-200 words. No plot, no characters' arcs.",
	);

	if (fixNotes != null && fixNotes.trim().length > 0) {
		parts.push("", "## Fix this problem from your previous attempt", fixNotes.trim());
	}

	return parts.join("\n");
}

export function LoreWorld_JSONSchema(): JSONSchemaLike {
	return {
		type: "object",
		required: ["name", "bible", "crazinessTolerance"],
		properties: {
			name: {type: "string", minLength: 1},
			bible: {type: "string", minLength: 1},
			crazinessTolerance: {type: "number"},
		},
	};
}
